import { listAllTherapistPatientsResponse } from "../modules/patient/application/patients";
import { useEffect, useState } from "react";
import { Search, User, Users } from "lucide-react";
import { GroupOfPeopleIcon } from "../shared/ui/icons/SubjectKindIcon";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { useTherapist } from "../context/useTherapist";
import { useSidebar } from "../context/useSidebar";
import FilterSelect from "../shared/ui/form/FilterSelect";
import { Patient } from "../modules/patient/domain/Patient";
import { motion } from "framer-motion";
import { CouplePatientAPI } from "../modules/patient/infrastructure/api/CouplePatientApiService";
import { CouplePatient } from "../modules/patient/domain/CouplePatient";
import { GroupPatientAPI } from "../modules/patient/infrastructure/api/GroupPatientApiService";
import { GroupPatient, groupLabel } from "../modules/patient/domain/GroupPatient";
import { useCourseLabels, useSessionPreferences } from "../modules/center";

export default function SidebarPatientSearch() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { therapist } = useTherapist();
    const { isExpanded, isHovered, isMobileOpen, toggleSidebar } = useSidebar();
    const [patients, setPatients] = useState<{ value: string; label: string }[]>([]);
    const [value, setValue] = useState("");
    // A training centre's student is named with the course they are on.
    const { labelOf } = useCourseLabels();
    // Couples and groups only where the centre runs them.
    const { modes } = useSessionPreferences();
    const { couple: couplesOn, group: groupsOn } = modes;

    const showFullInput = isExpanded || isHovered || isMobileOpen;


    useEffect(() => {
        if (!therapist?.id) return;

        Promise.all([
            listAllTherapistPatientsResponse(therapist.id),
            CouplePatientAPI.listByTherapist(therapist.id),
            // Groups are the newest of the three: a failure there must not
            // take the patients and couples down with it.
            GroupPatientAPI.list().catch((err) => {
                console.error(err);
                return { data: [] as GroupPatient[] };
            }),
        ])
            .then(([individualRes, coupleRes, groupRes]) => {
                const individualData = individualRes.data.patients || [];
                const coupleData = couplesOn ? coupleRes.data || [] : [];
                const groupData = groupsOn ? groupRes.data || [] : [];

                const combined = [
                    ...individualData.map((p: Patient) => ({
                        value: `patient:${p.id}`,
                        label: labelOf(p),
                        icon: <User className="w-4 h-4" />
                    })),
                    ...coupleData.map((c: CouplePatient) => ({
                        value: `couple:${c.id}`,
                        label: c.display_name,
                        icon: <Users className="w-4 h-4" />
                    })),
                    ...groupData.map((g: GroupPatient) => ({
                        value: `group:${g.id}`,
                        label: groupLabel(g),
                        icon: <GroupOfPeopleIcon className="w-4 h-4" />
                    }))
                ];
                setPatients(combined);
            })
            .catch(console.error);
    }, [therapist?.id, t, labelOf, couplesOn, groupsOn]);

    const handleSelect = (val: string | string[]) => {
        if (typeof val === "string" && val) {
            const [type, id] = val.split(":");
            if (type === "couple") {
                navigate(`/couple-patient/${id}`);
            } else if (type === "group") {
                navigate(`/group-patient/${id}`);
            } else {
                navigate(`/patient/${id}`);
            }
            setValue(""); // Reset after navigation
        }
    };

    if (!showFullInput) {
        return (
            <div
                className="flex items-center justify-center p-2 rounded-xl text-content-tertiary hover:text-brand-600 hover:bg-brand-50 cursor-pointer transition-colors"
                onClick={toggleSidebar}
            >
                <Search className="w-5 h-5" />
            </div>
        );
    }

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="relative z-20 mb-2 mt-2 px-1"
        >
            <div className="text-xs font-semibold uppercase text-content-tertiary mb-1.5 ml-1 tracking-wider">
                {t("sidebar.findPatient", "Find Patient")}
            </div>
            <FilterSelect
                icon={<Search className="w-4 h-4" />}
                options={patients}
                value={value}
                onChange={handleSelect}
                placeholder={t("sidebar.searchPatientPlaceholder", "Search...")}
                searchPlaceholder={t("patient.searchPlaceholder", "Search patient...")}
                className="w-full bg-gray-50 dark:bg-gray-800/50"
            />
        </motion.div>
    );
}
